import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Search, Filter, Calendar, User, Activity, Clock, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import { useWebSocket } from '../WebSocketContext';

const LogsPage = () => {
    const { isDark } = useTheme();
    const socket = useWebSocket();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(''); // 🔥 NOUVEAU: Recherche avec délai
    const [filterType, setFilterType] = useState('ALL');
    const [dateFilter, setDateFilter] = useState('TODAY');
    const [stats, setStats] = useState({
        actionStats: {},
        uniqueUsersCount: 0,
        totalLogs: 0
    });

    // Palette de couleurs réutilisable par famille d'action.
    const C = {
        green: 'text-green-600 bg-green-100 dark:bg-green-900/30',
        red: 'text-red-600 bg-red-100 dark:bg-red-900/30',
        blue: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30',
        orange: 'text-orange-600 bg-orange-100 dark:bg-orange-900/30',
        purple: 'text-purple-600 bg-purple-100 dark:bg-purple-900/30',
        gray: 'text-gray-600 bg-gray-100 dark:bg-gray-900/30',
        indigo: 'text-indigo-600 bg-indigo-100 dark:bg-indigo-900/30',
        yellow: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30',
    };

    // Types d'actions avec leurs couleurs et icônes
    const actionTypes = {
        // --- Site web (historique) ---
        LOGIN: { color: C.green, icon: '🟢', label: 'Connexion' },
        LOGOUT: { color: C.red, icon: '🔴', label: 'Déconnexion' },
        SERVICE_ADDED: { color: C.blue, icon: '➕', label: 'Service ajouté' },
        SERVICE_UPDATED: { color: C.orange, icon: '✏️', label: 'Service modifié' },
        SERVICE_DELETED: { color: C.red, icon: '🗑️', label: 'Service supprimé' },
        TOKEN_COPIED: { color: C.purple, icon: '📋', label: 'Token copié' },
        SEARCH_PERFORMED: { color: C.gray, icon: '🔍', label: 'Recherche' },
        SERVICES_LOADED: { color: C.indigo, icon: '📦', label: 'Services chargés' },
        EDIT_MODAL_OPENED: { color: C.yellow, icon: '👁️', label: 'Modal ouvert' },
        // --- Coffre : TOTP ---
        TOTP_CREATED: { color: C.blue, icon: '➕', label: 'TOTP créé' },
        LOGIN_AUTOFILL: { color: C.gray, icon: '⚡', label: 'Remplissage auto' },
        // --- Coffre : mots de passe ---
        PASSWORD_CREATED: { color: C.green, icon: '🔑', label: 'Mot de passe créé' },
        PASSWORD_UPDATED: { color: C.orange, icon: '✏️', label: 'Mot de passe modifié' },
        PASSWORD_DELETED: { color: C.red, icon: '🗑️', label: 'Mot de passe supprimé' },
        PASSWORD_SHARED: { color: C.blue, icon: '🔗', label: 'Mot de passe partagé' },
        PASSWORD_UNSHARED: { color: C.gray, icon: '🚫', label: 'Partage retiré' },
        // --- Coffre : passkeys ---
        PASSKEY_CREATED: { color: C.green, icon: '🔐', label: 'Passkey créée' },
        PASSKEY_USED: { color: C.purple, icon: '🔓', label: 'Passkey utilisée' },
        PASSKEY_DENIED: { color: C.red, icon: '⛔', label: 'Accès refusé' },
        PASSKEY_SHARED: { color: C.blue, icon: '🔗', label: 'Passkey partagée' },
        PASSKEY_UNSHARED: { color: C.gray, icon: '🚫', label: 'Partage retiré' },
        PASSKEY_LOGIN_SET: { color: C.orange, icon: '✏️', label: 'Identifiants passkey' },
        PASSKEY_DELETED: { color: C.red, icon: '🗑️', label: 'Passkey supprimée' },
        // --- Coffre : dossiers ---
        FOLDER_CREATED: { color: C.green, icon: '📁', label: 'Dossier créé' },
        FOLDER_UPDATED: { color: C.orange, icon: '✏️', label: 'Dossier modifié' },
        FOLDER_DELETED: { color: C.red, icon: '🗑️', label: 'Dossier supprimé' },
        FOLDER_ITEMS_ADDED: { color: C.blue, icon: '📥', label: 'Éléments classés' },
        FOLDER_ITEM_REMOVED: { color: C.gray, icon: '📤', label: 'Élément retiré' },
        FOLDER_MEMBER_SET: { color: C.blue, icon: '👤', label: 'Accès dossier accordé' },
        FOLDER_MEMBER_REMOVED: { color: C.gray, icon: '👤', label: 'Accès dossier retiré' },
        // --- Coffre : délégations ---
        DELEGATION_SET: { color: C.indigo, icon: '🤝', label: 'Délégation accordée' },
        DELEGATION_REMOVED: { color: C.gray, icon: '🤝', label: 'Délégation retirée' },
    };

    // 🔥 NOUVEAU: Debounce pour la recherche
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500); // Délai de 500ms

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Fonction pour recalculer les stats localement
    const updateStatsFromLogs = useCallback((logsList) => {
        const actionStats = {};
        const uniqueUsers = new Set();

        logsList.forEach(log => {
            actionStats[log.action] = (actionStats[log.action] || 0) + 1;
            uniqueUsers.add(log.userEmail);
        });

        setStats({
            actionStats,
            uniqueUsersCount: uniqueUsers.size,
            totalLogs: logsList.length
        });
    }, []);

    // 🔥 CORRIGÉ: Écouter les WebSockets pour mise à jour temps réel
    useEffect(() => {
        if (socket) {
            socket.on('logCreated', (newLog) => {
                console.log('📝 Nouveau log reçu:', newLog);

                // Ajouter le nouveau log en haut de la liste
                setLogs(prevLogs => {
                    const updatedLogs = [newLog, ...prevLogs];
                    // Recalculer les stats avec la nouvelle liste
                    updateStatsFromLogs(updatedLogs);
                    return updatedLogs;
                });
            });

            return () => {
                socket.off('logCreated');
            };
        }
    }, [socket, updateStatsFromLogs]);

    // 🔥 OPTIMISÉ: Charger les logs seulement quand debouncedSearchQuery change
    useEffect(() => {
        loadLogs();
    }, [debouncedSearchQuery, filterType, dateFilter]);

    const loadLogs = async () => {
        try {
            setLoading(true);

            // Construire les paramètres de requête
            const params = new URLSearchParams({
                limit: '1000',
                offset: '0'
            });

            if (filterType !== 'ALL') {
                params.append('action', filterType);
            }

            if (debouncedSearchQuery) {
                params.append('search', debouncedSearchQuery);
            }

            // Gestion des filtres de date
            const now = new Date();
            if (dateFilter === 'TODAY') {
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                params.append('startDate', today.toISOString());
            } else if (dateFilter === 'WEEK') {
                const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                params.append('startDate', weekAgo.toISOString());
            } else if (dateFilter === 'MONTH') {
                const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                params.append('startDate', monthAgo.toISOString());
            }

            const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;

            const response = await fetch(`/api/logs?${params.toString()}`, {
                headers: {
                    'x-user-email': user?.email || ''
                }
            });

            if (response.ok) {
                const data = await response.json();
                const logsList = data.logs || [];
                setLogs(logsList);

                // Calculer les stats initiales
                updateStatsFromLogs(logsList);
            } else {
                console.error('Erreur lors du chargement des logs:', response.status);
                setLogs([]);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des logs:', error);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const exportLogs = () => {
        const csvContent = "data:text/csv;charset=utf-8," +
            "Date,Utilisateur,Email,Action,Détails,IP\n" +
            logs.map(log =>
                `"${formatDate(log.timestamp)}","${log.userName}","${log.userEmail}","${actionTypes[log.action]?.label || log.action}","${String(getDetailsText(log.action, log.details)).replace(/"/g, '""')}","${log.ipAddress || ''}"`
            ).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `logs_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getActionBadge = (action) => {
        const config = actionTypes[action] || { color: 'text-gray-600 bg-gray-100', icon: '❓', label: action };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                <span>{config.icon}</span>
                {config.label}
            </span>
        );
    };

    const getDetailsText = (action, details) => {
        const d = details || {};
        const q = (v) => (v ? `« ${v} »` : null);
        // Nom d'un élément du coffre (passkey / mot de passe / dossier)
        const name = q(d.name) || 'un élément';
        const folder = q(d.folderName) || 'un dossier';
        const target = d.target || d.member || d.delegate || 'un utilisateur';
        // Formatage court d'une date d'expiration/échéance
        const when = (iso) => {
            if (!iso) return null;
            const dt = new Date(iso);
            return isNaN(dt) ? null : dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        };

        switch (action) {
            // --- Site web (historique) ---
            case 'LOGIN':
                return d.loginMethod === 'email_verification' ? 'Vérification par email' : 'Connexion';
            case 'LOGOUT':
                return d.logoutType === 'manual' ? 'Déconnexion manuelle' : 'Déconnexion automatique (inactivité)';
            case 'SERVICE_ADDED':
            case 'SERVICE_UPDATED':
            case 'SERVICE_DELETED':
            case 'TOKEN_COPIED':
            case 'EDIT_MODAL_OPENED':
                return d.serviceName || 'Service inconnu';
            case 'SEARCH_PERFORMED':
                return `"${d.searchQuery}" (${d.resultsCount} résultats)`;
            case 'SERVICES_LOADED':
                return `${d.servicesCount} services chargés (${d.userType})`;

            // --- Coffre : TOTP ---
            case 'TOTP_CREATED':
                return `A créé le TOTP ${name}`;
            case 'LOGIN_AUTOFILL':
                return `A rempli ses identifiants sur ${d.host || 'un site'}`;

            // --- Coffre : mots de passe ---
            case 'PASSWORD_CREATED':
                return `A créé le mot de passe ${name}`;
            case 'PASSWORD_UPDATED':
                return `A modifié le mot de passe ${name}`;
            case 'PASSWORD_DELETED':
                return `A supprimé le mot de passe ${name}`;
            case 'PASSWORD_SHARED':
                return `A partagé le mot de passe ${name} avec ${target}`;
            case 'PASSWORD_UNSHARED':
                return `A retiré le partage du mot de passe ${name} à ${target}`;

            // --- Coffre : passkeys ---
            case 'PASSKEY_CREATED':
                return `A créé la passkey ${name}`;
            case 'PASSKEY_USED':
                return `S'est connecté avec la passkey ${name}`;
            case 'PASSKEY_DENIED':
                return `Accès refusé à une passkey`;
            case 'PASSKEY_SHARED':
                return `A partagé la passkey ${name} avec ${target}`;
            case 'PASSKEY_UNSHARED':
                return `A retiré le partage de la passkey ${name} à ${target}`;
            case 'PASSKEY_LOGIN_SET':
                return `A renseigné les identifiants de la passkey ${name}`;
            case 'PASSKEY_DELETED':
                return `A supprimé la passkey ${name}`;

            // --- Coffre : dossiers ---
            case 'FOLDER_CREATED':
                return `A créé le dossier ${name}`;
            case 'FOLDER_UPDATED':
                return `A modifié le dossier ${name}`;
            case 'FOLDER_DELETED':
                return `A supprimé le dossier ${name}`
                    + (d.detached ? ` (${d.detached} élément${d.detached > 1 ? 's' : ''} détaché${d.detached > 1 ? 's' : ''})` : '');
            case 'FOLDER_ITEMS_ADDED':
                return `A classé ${d.count || 0} élément${d.count > 1 ? 's' : ''} dans le dossier ${folder}`;
            case 'FOLDER_ITEM_REMOVED':
                return `A retiré ${q(d.itemName) || 'un élément'} du dossier ${folder}`;
            case 'FOLDER_MEMBER_SET':
                return `A donné accès à ${target} sur le dossier ${folder}`
                    + ` (${d.allItems ? 'tous les éléments' : `${d.items || 0} élément${d.items > 1 ? 's' : ''}`}`
                    + (when(d.expiresAt) ? `, jusqu'au ${when(d.expiresAt)})` : ')');
            case 'FOLDER_MEMBER_REMOVED':
                return `A retiré l'accès de ${target} au dossier ${folder}`;

            // --- Coffre : délégations ---
            case 'DELEGATION_SET':
                return `A délégué la gestion à ${target}`
                    + (d.folderName ? ` sur le dossier ${folder}` : (d.folder ? ` sur un dossier` : ' (globale)'))
                    + (when(d.until) ? `, jusqu'au ${when(d.until)}` : '');
            case 'DELEGATION_REMOVED':
                return `A retiré une délégation`
                    + (d.delegate ? ` à ${d.delegate}` : '')
                    + (d.folderName ? ` sur le dossier ${folder}` : '');

            default:
                // Repli lisible : jamais de JSON brut à l'écran.
                return actionTypes[action]?.label || action.replace(/_/g, ' ').toLowerCase();
        }
    };

    const getRelativeTime = (timestamp) => {
        const now = new Date();
        const logTime = new Date(timestamp);
        const diffMs = now - logTime;
        const diffMinutes = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMinutes < 1) return 'À l\'instant';
        if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
        if (diffHours < 24) return `Il y a ${diffHours}h`;
        if (diffDays < 7) return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
        return formatDate(timestamp);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <div className="text-gray-500 dark:text-gray-400">Chargement des logs...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-4">
                        <div className="flex items-center gap-4">
                            <Link
                                to="/"
                                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                                title="Retour"
                            >
                                <ArrowLeft className="w-6 h-6 text-gray-500" />
                            </Link>
                            <div className="flex items-center gap-2">
                                <Activity className="w-6 h-6 text-blue-500" />
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    Logs d'activité
                                </h1>
                            </div>
                        </div>
                        <button
                            onClick={exportLogs}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors duration-200"
                        >
                            <Download className="w-4 h-4" />
                            Exporter CSV
                        </button>
                    </div>
                </div>
            </div>

            {/* Filtres */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 mb-6 border border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* 🔥 OPTIMISÉ: Recherche avec indicateur de chargement */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Rechercher utilisateur..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                            {/* Indicateur de recherche en cours */}
                            {searchQuery !== debouncedSearchQuery && (
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                </div>
                            )}
                        </div>

                        {/* Filtre par type */}
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            >
                                <option value="ALL">Toutes les actions</option>
                                <optgroup label="Site web">
                                    <option value="LOGIN">Connexions</option>
                                    <option value="LOGOUT">Déconnexions</option>
                                    <option value="SERVICE_ADDED">Services ajoutés</option>
                                    <option value="SERVICE_UPDATED">Services modifiés</option>
                                    <option value="SERVICE_DELETED">Services supprimés</option>
                                    <option value="TOKEN_COPIED">Tokens copiés</option>
                                    <option value="SEARCH_PERFORMED">Recherches</option>
                                    <option value="SERVICES_LOADED">Chargements</option>
                                </optgroup>
                                <optgroup label="Coffre — passkeys">
                                    <option value="PASSKEY_CREATED">Passkeys créées</option>
                                    <option value="PASSKEY_USED">Passkeys utilisées</option>
                                    <option value="PASSKEY_SHARED">Passkeys partagées</option>
                                    <option value="PASSKEY_DELETED">Passkeys supprimées</option>
                                    <option value="PASSKEY_DENIED">Accès refusés</option>
                                </optgroup>
                                <optgroup label="Coffre — mots de passe / TOTP">
                                    <option value="PASSWORD_CREATED">Mots de passe créés</option>
                                    <option value="PASSWORD_UPDATED">Mots de passe modifiés</option>
                                    <option value="PASSWORD_DELETED">Mots de passe supprimés</option>
                                    <option value="PASSWORD_SHARED">Mots de passe partagés</option>
                                    <option value="TOTP_CREATED">TOTP créés</option>
                                </optgroup>
                                <optgroup label="Coffre — dossiers / délégations">
                                    <option value="FOLDER_CREATED">Dossiers créés</option>
                                    <option value="FOLDER_UPDATED">Dossiers modifiés</option>
                                    <option value="FOLDER_DELETED">Dossiers supprimés</option>
                                    <option value="FOLDER_MEMBER_SET">Accès accordés</option>
                                    <option value="FOLDER_MEMBER_REMOVED">Accès retirés</option>
                                    <option value="DELEGATION_SET">Délégations accordées</option>
                                    <option value="DELEGATION_REMOVED">Délégations retirées</option>
                                </optgroup>
                            </select>
                        </div>

                        {/* Filtre par date */}
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <select
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            >
                                <option value="ALL">Toute la période</option>
                                <option value="TODAY">Aujourd'hui</option>
                                <option value="WEEK">Cette semaine</option>
                                <option value="MONTH">Ce mois</option>
                            </select>
                        </div>
                    </div>

                    {/* 🔥 NOUVEAU: Indicateur de recherche active */}
                    {searchQuery && (
                        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                            <div className="flex items-center gap-2">
                                <Search className="w-4 h-4 text-blue-600" />
                                <span className="text-sm text-blue-700 dark:text-blue-300">
                                    Recherche active : "<strong>{searchQuery}</strong>"
                                    {searchQuery !== debouncedSearchQuery && (
                                        <span className="ml-2 text-xs text-blue-500">(recherche en cours...)</span>
                                    )}
                                </span>
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="ml-auto text-blue-600 hover:text-blue-800 text-sm"
                                >
                                    Effacer
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Statistiques */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                                <Activity className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
                                <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.totalLogs}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Connexions</p>
                                <p className="text-xl font-bold text-gray-900 dark:text-white">
                                    {stats.actionStats.LOGIN || 0}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                                <Clock className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Tokens copiés</p>
                                <p className="text-xl font-bold text-gray-900 dark:text-white">
                                    {stats.actionStats.TOKEN_COPIED || 0}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-orange-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Utilisateurs uniques</p>
                                <p className="text-xl font-bold text-gray-900 dark:text-white">
                                    {stats.uniqueUsersCount}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Liste des logs */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-900/50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Date & Heure
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Utilisateur
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Action
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Détails
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        IP
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        JSON (debug)
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {logs.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                                            <div className="flex flex-col items-center gap-2">
                                                <Activity className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                                                <p>Aucun log trouvé</p>
                                                <p className="text-sm">Essayez de modifier vos filtres</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map((log, index) => (
                                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <div className="text-gray-900 dark:text-white">
                                                    {formatDate(log.timestamp)}
                                                </div>
                                                <div className="text-gray-500 dark:text-gray-400 text-xs">
                                                    {getRelativeTime(log.timestamp)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {log.userName}
                                                    </div>
                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                        {log.userEmail}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getActionBadge(log.action)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                                {getDetailsText(log.action, log.details)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                                {log.ipAddress || '-'}
                                            </td>
                                            <td className="px-6 py-4 text-xs text-gray-400 dark:text-gray-500 font-mono">
                                                <pre className="max-w-xs max-h-24 overflow-auto whitespace-pre-wrap break-all bg-gray-50 dark:bg-gray-900/50 rounded p-2 m-0">
                                                    {(() => { try { return JSON.stringify(log.details, null, 2); } catch { return String(log.details); } })()}
                                                </pre>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Légende */}
                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Légende des actions</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        {Object.entries(actionTypes).map(([key, config]) => (
                            <div key={key} className="flex items-center gap-1">
                                <span>{config.icon}</span>
                                <span className="text-gray-600 dark:text-gray-400">{config.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LogsPage;